import {
    allSurchargeRevisionsTqo,
    SurchargeableClassname,
    useAllSurchargeRevisions,
    useSurchargeById,
    useSurchargeByIdAsof
} from "../rpc/webrpc-client.gen.ts";
import {DateArg, formatISO} from "date-fns";
import {useRefreshableQuery} from "../util/tqrefresh.tsx";
import {NADA} from "../util/arutil.tsx";
import {RevisionBrowser, RevisionStub} from "../components/RevisionBrowser.tsx";
import {SurchargeEditor} from "./editor.tsx";
import {SuspenseDialog} from "../components/SuspenseDialog.tsx";
import {spawnTstzSpan} from "../tstzspan.ts";
import {Stack} from "@mui/joy";
import {unknownAuthor} from "../agent/author.tsx";
import {useLiveLastValidSurchargeById, useMothership} from "../tq/tqx.tsx";

export interface ActiveSurchargeCargoProps {
    subjectClass: SurchargeableClassname;
    surchargeId: string;
    active: true;
    onAsofChange?: (newAsof: DateArg<Date> | undefined) => void;
    afterPatch?: () => void;
}

export function ActiveSurchargeCargoView(
    {
        subjectClass,
        surchargeId,
        onAsofChange,
        afterPatch,
    }: ActiveSurchargeCargoProps) {
    const mama = useMothership();
    const surchargeTqrv = useSurchargeById({subjectClass, surchargeId});

    const {data: revisions} = useRefreshableQuery(allSurchargeRevisionsTqo(mama, {subjectClass, surchargeId}));
    const revStubs: RevisionStub[] = (revisions ?? []).map(rev => {
        return {...rev, author: rev.author ?? unknownAuthor, validSpan: spawnTstzSpan(rev.validSpan)}
    })

    if (surchargeTqrv.isPending) {
        return null;
    }

    const surcharge = surchargeTqrv.data;
    const revs = revisions ?? NADA;

    if (!surcharge) {
        return null;
    }

    return <Stack direction="column">
        {revs.length > 1
            ? <RevisionBrowser revisions={revStubs}
                               onSelectionChange={(selectedRevision) => {
                                   if (onAsofChange) {
                                       const insideRev = selectedRevision.validSpan.guaranteedIncluded();
                                       if (insideRev) {
                                           onAsofChange(insideRev);
                                       }
                                   }
                               }}/>
            : null}
        <SurchargeEditor surcharge={surcharge} mode="rw" afterPatch={afterPatch}/>
    </Stack>
}

export interface SurchargeCargoHistoryProps {
    subjectClass: SurchargeableClassname;
    surchargeId: string;
    active: false;
    asof?: DateArg<Date>;
    onAsofChange?: (newAsof: DateArg<Date> | undefined) => void;
}

export function SurchargeCargoHistoryView(
    {
        subjectClass,
        surchargeId,
        asof: inAsof,
        onAsofChange,
    }: SurchargeCargoHistoryProps) {
    const asof = inAsof ?? new Date();
    const isoAsof = formatISO(asof);
    const surchargeTqrv = useSurchargeByIdAsof({subjectClass, surchargeId, asof: isoAsof});
    const revisionsTqrv
        = useAllSurchargeRevisions({subjectClass, surchargeId});

    const surcharge = surchargeTqrv.data;

    if (!surchargeTqrv.isSuccess || !revisionsTqrv.isSuccess || !surcharge) {
        return null;
    }

    const revisions = revisionsTqrv.data;
    const revStubs: RevisionStub[] = revisions.map(rev => {
        return {...rev, author: rev.author ?? unknownAuthor, validSpan: spawnTstzSpan(rev.validSpan)}
    })
    return <Stack direction="column">
        <RevisionBrowser revisions={revStubs}
                         asof={asof}
                         onSelectionChange={(selectedRevision) => {
                             if (onAsofChange) {
                                 const insideRev = selectedRevision.validSpan.guaranteedIncluded();
                                 onAsofChange(insideRev);
                             }
                         }}/>
        <SurchargeEditor surcharge={surcharge} mode="r"/>
    </Stack>
}

export type SurchargeCargoProps = ActiveSurchargeCargoProps | SurchargeCargoHistoryProps;

export function SurchargeCargoView(props: SurchargeCargoProps) {
    const {subjectClass, surchargeId, active, ...rest} = props;

    const surchargeTqrv
        = useLiveLastValidSurchargeById({subjectClass, surchargeId});
    if (!surchargeTqrv.isSuccess) {
        return <SuspenseDialog message="fetching surcharge"/>;
    }
    const activeValidSpan = spawnTstzSpan(surchargeTqrv.data.validSpan);
    const isActive = active || activeValidSpan.contains(props.asof);

    if (isActive) {
        return <ActiveSurchargeCargoView subjectClass={subjectClass} surchargeId={surchargeId}
                                         active={true} {...rest}/>;
    } else {
        return <SurchargeCargoHistoryView subjectClass={subjectClass} surchargeId={surchargeId}
                                          active={false} {...rest}/>
    }
}

